import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Plus, Pencil, Trash2, X } from 'lucide-react';
import { useServiceViewModel } from '../../view-model/professional/company/service-view-model';
import { optionCategoryViewModel } from '../../view-model/professional/company/option-category-view-model';
import CategoryForm from './category-form';
import ConfirmationModal from '../ui/confirmation-modal';
import { OptionCategory } from '../../model/professional/option-category-model';

interface ServiceCategoriesStepProps {
  serviceId: string;
}

const ServiceCategoriesStep = observer((props: ServiceCategoriesStepProps) => {
  const { serviceId } = props;
  const viewModel = useServiceViewModel();
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<OptionCategory | null>(null);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    categoryId: string | null;
  }>({ isOpen: false, categoryId: null });

  const categories = viewModel.getCategoriesByServiceId(serviceId);

  const handleAddCategory = () => {
    setEditingCategory(null);
    setShowForm(true);
  };

  const handleEditCategory = (category: OptionCategory) => {
    setEditingCategory(category);
    setShowForm(true);
  };

  const handleDeleteCategory = async () => {
    if (deleteModal.categoryId) {
      await optionCategoryViewModel.deleteCategory(deleteModal.categoryId);
      viewModel.getCategoriesByServiceId(serviceId);
      setDeleteModal({ isOpen: false, categoryId: null });
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingCategory(null);
    viewModel.getCategoriesByServiceId(serviceId);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingCategory(null);
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-auto p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Catégories d'options
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Organisez vos options en catégories (couleur, taille, matériau, etc.). Cette étape est facultative.
          </p>
        </div>

        {/* Liste des catégories */}
        {categories.length > 0 && !showForm && (
          <div className="space-y-3 mb-4">
            {categories.map((category) => (
              <div
                key={category.id}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-primary/50 transition-colors"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      {category.name}
                    </h4>
                    {category.description && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {category.description}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-2 mt-2">
                      {category.isRequired && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                          Obligatoire
                        </span>
                      )}
                      {category.isMultipleChoice && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                          Choix multiple
                        </span>
                      )}
                      {!category.isActive && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                          Inactive
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-1 ml-4">
                    <button
                      onClick={() => handleEditCategory(category)}
                      className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-primary transition-colors"
                      title="Modifier"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={() => setDeleteModal({ isOpen: true, categoryId: category.id })}
                      className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-red-500 transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Formulaire d'ajout/édition */}
        {showForm ? (
          <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-medium text-gray-900 dark:text-white">
                {editingCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
              </h4>
              <button
                onClick={handleCancelForm}
                className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                <X size={18} />
              </button>
            </div>
            <CategoryForm
              mode={editingCategory ? 'edit' : 'create'}
              serviceId={serviceId}
              category={editingCategory || undefined}
              onSuccess={handleFormSuccess}
            />
          </div>
        ) : (
          <button
            onClick={handleAddCategory}
            className="w-full border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 hover:border-primary hover:bg-primary/5 transition-all group"
          >
            <div className="flex flex-col items-center justify-center text-gray-500 dark:text-gray-400 group-hover:text-primary transition-colors">
              <div className="bg-gray-100 dark:bg-gray-800 rounded-full p-3 mb-2 group-hover:bg-primary/10 transition-colors">
                <Plus size={24} className="text-gray-400 group-hover:text-primary transition-colors" />
              </div>
              <span className="font-medium">Ajouter une catégorie</span>
              <span className="text-sm mt-1">Ex: Couleur, Taille, Matériau...</span>
            </div>
          </button>
        )}

        {/* Message si aucune catégorie et pas de formulaire */}
        {categories.length === 0 && !showForm && (
          <div className="text-center py-8">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Aucune catégorie pour le moment. Vous pouvez passer à l'étape suivante ou en ajouter.
            </p>
          </div>
        )}
      </div>

      {/* Modal de confirmation de suppression */}
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, categoryId: null })}
        onConfirm={handleDeleteCategory}
        title="Supprimer la catégorie"
        message="Êtes-vous sûr de vouloir supprimer cette catégorie ? Cette action est irréversible."
        type="warning"
        confirmText="Supprimer"
        cancelText="Annuler"
      />
    </div>
  );
});

export default ServiceCategoriesStep;
